import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';
import Button from '../../components/UI/Button';

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-green) 0%,
    var(--secondary-blue) 100%
  );
  padding: var(--space-lg);
`;

const AuthCard = styled(motion.div)`
  background: white;
  border-radius: var(--radius-2xl);
  padding: var(--space-3xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 400px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-2xl);
  
  h1 {
    font-size: 2rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  p {
    color: var(--neutral-gray-600);
  }
`;

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const InputGroup = styled.div`
  position: relative;
  
  input {
    width: 100%;
    padding: var(--space-md) var(--space-md) var(--space-md) 3rem;
    border: 1px solid var(--neutral-gray-300);
    border-radius: var(--radius-lg);
    font-size: 1rem;
    
    &:focus {
      border-color: var(--primary-green);
      box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
    }
    
    &.error {
      border-color: var(--accent-orange);
    }
  }
  
  .icon {
    position: absolute;
    left: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--neutral-gray-400);
  }
  
  .toggle-password {
    position: absolute;
    right: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--neutral-gray-400);
    cursor: pointer;
    padding: 0;
    
    &:hover {
      color: var(--neutral-gray-600);
    }
  }
  
  .error-message {
    color: var(--accent-orange);
    font-size: 0.875rem;
    margin-top: var(--space-sm);
  }
`;

const AuthFooter = styled.div`
  margin-top: var(--space-xl);
  text-align: center;
  
  p {
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-md);
  }
  
  a {
    color: var(--primary-green);
    font-weight: 500;
    
    &:hover {
      color: var(--primary-green-dark);
    }
  }
`;

const Register = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock user data
      const userData = {
        id: 1,
        name: formData.name,
        email: formData.email,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      };
      
      onLogin(userData);
      navigate('/dashboard');
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <AuthHeader>
          <h1>Join Our Community!</h1>
          <p>Create an account to access exclusive content and share your adventures</p>
        </AuthHeader>

        <AuthForm onSubmit={handleSubmit}>
          <InputGroup>
            <FiUser className="icon" size={20} />
            <input
              type="text"
              name="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </InputGroup>

          <InputGroup>
            <FiMail className="icon" size={20} />
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </InputGroup>

          <InputGroup>
            <FiLock className="icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
            {errors.password && <div className="error-message">{errors.password}</div>}
          </InputGroup>

          <InputGroup>
            <FiLock className="icon" size={20} />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
            {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
          </InputGroup>

          {errors.submit && (
            <div style={{ color: 'var(--accent-orange)', textAlign: 'center' }}>
              {errors.submit}
            </div>
          )}

          <Button type="submit" fullWidth disabled={isLoading}>
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </AuthForm>

        <AuthFooter>
          <p>Already have an account?</p>
          <Link to="/login">Sign in here</Link>
        </AuthFooter>
      </AuthCard>
    </AuthContainer>
  );
};

export default Register;
